import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  TextField, 
  Button, 
  CircularProgress, 
  Typography, 
  Box, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { handleJsonToFile } from 'store/interactions/thunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import config from "../../../config";
import * as Sentry from '@sentry/react';
import { useConfirm } from 'components/UI/confirmProvider';
import { apis } from 'utils/lib/apis';
import Acent from 'components/Acent';
import { Dropdown, Button as AntButton } from 'antd';
import { SPANISH_ITEM_LOWER, SPANISH_ITEM_UPPER } from "utils/config/reference";
import color from 'utils/constants/color';

const AcentModalWrapper = ({ inputRef, setValue, isDisabled = false }) => {
  const [upperMenuVisible, setUpperMenuVisible] = useState(false);
  const [lowerMenuVisible, setLowerMenuVisible] = useState(false);

  const handleUpperMenuClick = (e) => {
    insertAccent(e.key);
    setUpperMenuVisible(false);
  };

  const handleLowerMenuClick = (e) => {
    insertAccent(e.key);
    setLowerMenuVisible(false);
  };

  const insertAccent = (accent) => {
    if (!inputRef) {
      return;
    }
    const textarea = inputRef.current ? inputRef.current : inputRef;
    if (!textarea) return;

    try {
      const { selectionStart, selectionEnd, value: inputValue } = textarea;
      const newInputValue = inputValue.substring(0, selectionStart) + accent + inputValue.substring(selectionEnd);

      updateTextareaValue(textarea, newInputValue);
      dispatchInputEvent(textarea);
      updateCursorPosition(textarea, selectionStart + accent.length);
    } catch (error) {
      console.log("Error in accent insertion:", error);
      Sentry.captureException(`136 Error in accent insertion: ${error}`);
    }
  };

  const updateTextareaValue = (textarea, newValue) => {
    textarea.value = newValue;
    if (setValue) {
      setValue(newValue);
    }
  };

  const dispatchInputEvent = (textarea) => {
    const event = document.createEvent('Event');
    event.initEvent('input', true, true);
    textarea.dispatchEvent(event);
  };

  const updateCursorPosition = (textarea, newPosition) => {
    setTimeout(() => {
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 0);
  };

  const Uppermenu = {
    items: SPANISH_ITEM_UPPER.map((item) => ({
      key: item,
      label: item,
      onClick: handleUpperMenuClick
    }))
  };

  const Lowermenu = {
    items: SPANISH_ITEM_LOWER.map((item) => ({
      key: item,
      label: item,
      onClick: handleLowerMenuClick
    }))
  };

  return (
    <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
      <Dropdown
        disabled={isDisabled}
        menu={Uppermenu}
        onOpenChange={(flag) => setUpperMenuVisible(flag)}
        open={upperMenuVisible}
        trigger={['click']}
        getPopupContainer={() => document.body}
      >
        <AntButton 
          size="small" 
          type="primary" 
          style={{ backgroundColor: color.green100, borderColor: color.green100 }}
          onClick={() => setUpperMenuVisible(!upperMenuVisible)}
        >
          Á
        </AntButton>
      </Dropdown>
      <Dropdown
        disabled={isDisabled}
        menu={Lowermenu}
        onOpenChange={(flag) => setLowerMenuVisible(flag)}
        open={lowerMenuVisible}
        trigger={['click']}
        getPopupContainer={() => document.body}
      >
        <AntButton 
          size="small" 
          type="primary" 
          style={{ backgroundColor: color.green100, borderColor: color.green100 }}
          onClick={() => setLowerMenuVisible(!lowerMenuVisible)}
        >
          á
        </AntButton>
      </Dropdown>
    </Box>
  );
};

const getUrls = (context) => {
  const isDev = config.isDev;
  const baseUrl = 'https://storage.googleapis.com/yo-americano.appspot.com/json_data/';
  
  const contextMap = {
    common: {
      wordsOnly: isDev 
        ? `${baseUrl}prohibated_word_test.json`
        : `${baseUrl}prohibated_word.json`,
      wordsWithSynonyms: isDev
        ? `${baseUrl}prohibated_word_with_synonyms_test.json`
        : `${baseUrl}prohibated_word_with_synonyms.json`
    },
    vocabulary: {
      wordsOnly: `${baseUrl}prohibated_word_vocabulary_${isDev ? 'test' : 'prod'}.json`,
      wordsWithSynonyms: `${baseUrl}prohibated_word_vocabulary_with_synonyms_${isDev ? 'test' : 'prod'}.json`
    },
    story: {
      wordsOnly: `${baseUrl}prohibated_word_story_${isDev ? 'test' : 'prod'}.json`,
      wordsWithSynonyms: `${baseUrl}prohibated_word_story_with_synonyms_${isDev ? 'test' : 'prod'}.json`
    },
    assignment: {
      wordsOnly: `${baseUrl}prohibated_word_assignment_${isDev ? 'test' : 'prod'}.json`,
      wordsWithSynonyms: `${baseUrl}prohibated_word_assignment_with_synonyms_${isDev ? 'test' : 'prod'}.json`
    }
  };

  return contextMap[context] || contextMap.common;
};

export default function InappropriateWordsEditor({ context = 'common' }) {
  const showAlert = useAlert();
  const [wordsData, setWordsData] = useState([]);
  const [originalWordsData, setOriginalWordsData] = useState([]);
  const [commonWords, setCommonWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const confirm = useConfirm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [modalWord, setModalWord] = useState('');
  const [modalSynonyms, setModalSynonyms] = useState('');
  const [generatingSynonyms, setGeneratingSynonyms] = useState(false);
  const [generatingAllSynonyms, setGeneratingAllSynonyms] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('added_asc');
  const debounceTimerRef = useRef(null);
  const wordInputRef = useRef(null);
  const synonymInputRef = useRef(null);

  useEffect(() => {
    setWordsData([]);
    setOriginalWordsData([]);
    setCommonWords([]);
    setSearchTerm('');
    setError(null);
    loadData();
  }, [context]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ant-dropdown {
        z-index: 1401 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ant-dropdown {
        z-index: 1401 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const loadCommonWords = async () => {
    try {
      const commonUrls = getUrls('common');
      let commonData = null;
      let response = await fetch(commonUrls.wordsWithSynonyms + '?' + `t=${Date.now()}`);
      
      if (response.ok) {
        commonData = await response.json();
      } else {
        response = await fetch(commonUrls.wordsOnly + '?' + `t=${Date.now()}`);
        if (response.ok) {
          commonData = await response.json();
        }
      }
      
      if (commonData) {
        const formattedCommonData = Array.isArray(commonData) 
          ? commonData.map(item => ({
              word: typeof item === 'string' ? item.toLowerCase() : (item.word || '').toLowerCase(),
              synonyms: typeof item === 'string' ? [] : (Array.isArray(item.synonyms) ? item.synonyms : [])
            }))
          : [];
        setCommonWords(formattedCommonData);
      } else {
        setCommonWords([]);
      }
    } catch (err) {
      console.error('Error loading common words:', err);
      setCommonWords([]);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (context !== 'common') {
        await loadCommonWords();
      } else {
        setCommonWords([]);
      }
      
      const urls = getUrls(context);
      let data = null;
      let response = await fetch(urls.wordsWithSynonyms + '?' + `t=${Date.now()}`);
      
      if (response.ok) {
        data = await response.json();
      } else {
        response = await fetch(urls.wordsOnly + '?' + `t=${Date.now()}`);
        if (response.ok) {
          data = await response.json();
        }
      }
      
      if (data) {
        const formattedData = Array.isArray(data) 
          ? data.map(item => ({
              word: typeof item === 'string' ? item : (item.word || ''),
              synonyms: typeof item === 'string' ? [] : (Array.isArray(item.synonyms) ? item.synonyms : [])
            }))
          : [];
        setWordsData(formattedData);
        setOriginalWordsData(formattedData);
      } else {
        setWordsData([]);
        setOriginalWordsData([]);
      }
    } catch (err) {
        console.error(err);
        setError('Failed to load data.');
        Sentry.captureException(`269 Error in InappropriateWordsEditor: ${err}`);
      setWordsData([]);
      setOriginalWordsData([]);
    } finally {
        setLoading(false);
    }
  };

  const filteredWordsData = wordsData.filter(item => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.word.toLowerCase().includes(search) ||
      (item.synonyms && item.synonyms.length > 0 && item.synonyms[0].toLowerCase().includes(search))
    );
  });

  const getSortedWords = useCallback((words) => {
    const sorted = [...words];
    
    switch (sortBy) {
      case 'alphabetical_asc':
        return sorted.sort((a, b) => a.word.localeCompare(b.word));
      case 'alphabetical_desc':
        return sorted.sort((a, b) => b.word.localeCompare(a.word));
      case 'added_asc': {
        const originalMap = new Map(originalWordsData.map((item, index) => [item.word, index]));
        const wordsDataMap = new Map(wordsData.map((item, index) => [item.word, index]));
        return sorted.sort((a, b) => {
          const indexA = originalMap.has(a.word) 
            ? originalMap.get(a.word) 
            : (wordsDataMap.get(a.word) ?? 0) + originalWordsData.length;
          const indexB = originalMap.has(b.word) 
            ? originalMap.get(b.word) 
            : (wordsDataMap.get(b.word) ?? 0) + originalWordsData.length;
          return indexA - indexB;
        });
      }
      case 'added_desc': {
        const originalMap = new Map(originalWordsData.map((item, index) => [item.word, index]));
        const wordsDataMap = new Map(wordsData.map((item, index) => [item.word, index]));
        return sorted.sort((a, b) => {
          const indexA = originalMap.has(a.word) 
            ? originalMap.get(a.word) 
            : (wordsDataMap.get(a.word) ?? 0) + originalWordsData.length;
          const indexB = originalMap.has(b.word) 
            ? originalMap.get(b.word) 
            : (wordsDataMap.get(b.word) ?? 0) + originalWordsData.length;
          return indexB - indexA;
        });
      }
      default:
        return sorted;
    }
  }, [sortBy, originalWordsData, wordsData]);

  const sortedAndFilteredWords = getSortedWords(filteredWordsData);

  const getDiff = () => {
    const originalMap = new Map(originalWordsData.map(item => [item.word, item]));
    const currentMap = new Map(wordsData.map(item => [item.word, item]));
    
    const added = [];
    const removed = [];
    const updated = [];
    
    wordsData.forEach(item => {
      const original = originalMap.get(item.word);
      if (!original) {
        added.push(item);
      } else if (JSON.stringify(original.synonyms || []) !== JSON.stringify(item.synonyms || [])) {
        updated.push(item);
      }
    });
    
    originalWordsData.forEach(item => {
      if (!currentMap.has(item.word)) {
        removed.push(item);
      }
    });
    
    return { added, removed, updated };
  };

  const saveToJson = useCallback(async (dataToSave) => {
    setSaving(true);
    setError(null);
    try {
      const urls = getUrls(context);
      const wordsOnly = dataToSave.map(item => item.word);
      const wordsWithSynonyms = dataToSave.map(item => ({
        word: item.word,
        synonyms: Array.isArray(item.synonyms) ? item.synonyms : []
      }));

      const [wordsOnlyResponse, wordsWithSynonymsResponse] = await Promise.all([
        handleJsonToFile({
          url: urls.wordsOnly,
          json: wordsOnly,
          filename: `prohibited_words_${context}.json`
        }),
        handleJsonToFile({
          url: urls.wordsWithSynonyms,
          json: wordsWithSynonyms,
          filename: `prohibited_words_${context}_with_synonyms.json`
        })
      ]);

      if (wordsOnlyResponse?.success && wordsWithSynonymsResponse?.success) {
        showAlert('Word list updated successfully.', 'success');
        setOriginalWordsData(wordsWithSynonyms.map(item => ({
          word: item.word,
          synonyms: [...(item.synonyms || [])]
        })));
        return true;
      } else {
        setError('Failed to save word list.');
        showAlert('Failed to save word list.', 'error');
        return false;
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(`270 Error saving word list: ${err}`);
      setError(err.message || 'Error saving data.');
      showAlert('Error saving word list.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [showAlert,context]);

  const debouncedSave = useCallback((dataToSave) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      saveToJson(dataToSave);
    }, 1000);
  }, [saveToJson]);

  const handleSave = async () => {
    const { added, removed, updated } = getDiff();

    if (added.length === 0 && removed.length === 0 && updated.length === 0) {
      showAlert("No changes to save.", 'info');
      return;
    }

    confirm({
      title: 'Confirm Save Changes',
      content: (
        <div>
          <p><strong>Added:</strong> {added.length ? added.map(a => a.word).join(', ') : 'None'}</p>
          <p><strong>Updated:</strong> {updated.length ? updated.map(u => u.word).join(', ') : 'None'}</p>
          <p><strong>Removed:</strong> {removed.length ? removed.map(r => r.word).join(', ') : 'None'}</p>
          <p>Do you want to save these changes?</p>
        </div>
      ),
      okText: 'Yes, Save',
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        setError(null);
        try {
          const wordsOnly = wordsData.map(item => item.word);
          const wordsWithSynonyms = wordsData.map(item => ({
            word: item.word,
            synonyms: Array.isArray(item.synonyms) ? item.synonyms : []
          }));

          const urls = getUrls(context);
          const [wordsOnlyResponse, wordsWithSynonymsResponse] = await Promise.all([
            handleJsonToFile({
              url: urls.wordsOnly,
              json: wordsOnly,
              filename: `prohibited_words_${context}.json`
            }),
            handleJsonToFile({
              url: urls.wordsWithSynonyms,
              json: wordsWithSynonyms,
              filename: `prohibited_words_${context}_with_synonyms.json`
            })
          ]);

          if (wordsOnlyResponse?.success && wordsWithSynonymsResponse?.success) {
            showAlert('Word list saved successfully.', 'success');
            setOriginalWordsData(wordsWithSynonyms.map(item => ({
              word: item.word,
              synonyms: [...(item.synonyms || [])]
            })));
          } else {
            showAlert('Failed to save word list.', 'error');
          }
        } catch (err) {
          console.error(err);
          Sentry.captureException(`270 Error in InappropriateWordsEditor: ${err}`);
          setError(err.message || 'Error saving data.');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const handleOpenModal = (index = null) => {
    if (index !== null) {
      const item = wordsData[index];
      setEditingIndex(index);
      setModalWord(item.word);
      setModalSynonyms(Array.isArray(item.synonyms) && item.synonyms.length > 0 ? item.synonyms[0] : '');
    } else {
      setEditingIndex(null);
      setModalWord('');
      setModalSynonyms('');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingIndex(null);
    setModalWord('');
    setModalSynonyms('');
  };

  const handleGenerateSynonyms = async () => {
    if (!modalWord.trim()) {
      showAlert('Please enter a word first.', 'warning');
      return;
    }

    setGeneratingSynonyms(true);
    try {
      const result = await apis.post('vocabularyRDS', '/generate_synonyms', {
        body: { word: modalWord.trim() }
      });
      
      if (result && result.success) {
        const synonyms = result.data ? result.data : [];
        setModalSynonyms(synonyms.length > 0 ? synonyms[0] : '');
        showAlert('Synonym generated successfully.', 'success');
      } else {
        showAlert(result?.error || 'Failed to generate synonym.', 'error');
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(`271 Error generating synonyms: ${err}`);
      showAlert('Error generating synonyms.', 'error');
    } finally {
      setGeneratingSynonyms(false);
    }
  };

  const handleGenerateAllSynonyms = async () => {
    const wordsWithoutSynonyms = wordsData.filter(item => 
      !item.synonyms || !Array.isArray(item.synonyms) || item.synonyms.length === 0
    );

    if (wordsWithoutSynonyms.length === 0) {
      showAlert('All words already have synonyms.', 'info');
      return;
    }

    confirm({
      title: 'Generate Synonyms for All Words',
      content: `This will generate synonyms for ${wordsWithoutSynonyms.length} word(s) that currently have no synonyms. This may take a while. Continue?`,
      okText: 'Yes, Generate',
      cancelText: 'Cancel',
      onOk: async () => {
        setGeneratingAllSynonyms(true);
        setGenerationProgress({ current: 0, total: wordsWithoutSynonyms.length });
        setError(null);

        const updatedWordsData = [...wordsData];
        let successCount = 0;
        let failCount = 0;

        try {
          for (let i = 0; i < wordsWithoutSynonyms.length; i++) {
            const wordItem = wordsWithoutSynonyms[i];
            const wordIndex = updatedWordsData.findIndex(w => w.word === wordItem.word);
            
            if (wordIndex === -1) continue;

            setGenerationProgress({ current: i + 1, total: wordsWithoutSynonyms.length });

            try {
              const result = await apis.post('vocabularyRDS', '/generate_synonyms', {
                body: { word: wordItem.word.trim() }
              });
              
              if (result && result.success) {
                const synonyms = result.data ? result.data : [];
                if (synonyms.length > 0) {
                  updatedWordsData[wordIndex] = {
                    ...updatedWordsData[wordIndex],
                    synonyms: [synonyms[0]]
                  };
                  successCount++;
                } else {
                  failCount++;
                }
              } else {
                failCount++;
              }
            } catch (err) {
              console.error(`Error generating synonym for ${wordItem.word}:`, err);
              failCount++;
            }

            await new Promise(resolve => setTimeout(resolve, 200));
          }

          setWordsData(updatedWordsData);

          const wordsOnly = updatedWordsData.map(item => item.word);
          const wordsWithSynonyms = updatedWordsData.map(item => ({
            word: item.word,
            synonyms: Array.isArray(item.synonyms) ? item.synonyms : []
          }));

          const urls = getUrls(context);
          const [wordsOnlyResponse, wordsWithSynonymsResponse] = await Promise.all([
            handleJsonToFile({
              url: urls.wordsOnly,
              json: wordsOnly,
              filename: `prohibited_words_${context}.json`
            }),
            handleJsonToFile({
              url: urls.wordsWithSynonyms,
              json: wordsWithSynonyms,
              filename: `prohibited_words_${context}_with_synonyms.json`
            })
          ]);

          if (wordsOnlyResponse?.success && wordsWithSynonymsResponse?.success) {
            setOriginalWordsData(wordsWithSynonyms.map(item => ({
              word: item.word,
              synonyms: [...(item.synonyms || [])]
            })));
            showAlert(
              `Synonyms generated: ${successCount} successful, ${failCount} failed.`,
              successCount > 0 ? 'success' : 'warning'
            );
          } else {
            showAlert('Synonyms generated but failed to save.', 'error');
          }
        } catch (err) {
          console.error(err);
          Sentry.captureException(`272 Error generating all synonyms: ${err}`);
          showAlert('Error generating synonyms.', 'error');
        } finally {
          setGeneratingAllSynonyms(false);
          setGenerationProgress({ current: 0, total: 0 });
        }
      }
    });
  };

  const handleAddWord = async () => {
    if (!modalWord.trim()) {
      showAlert('Please enter a word.', 'warning');
      return;
    }

    const word = modalWord.trim().toLowerCase();

    if (!word) {
      showAlert('Please enter a valid word.', 'warning');
      return;
    }

    const synonymInput = modalSynonyms.trim().toLowerCase();
    const synonymParts = synonymInput.split(/\s+/);
    
    let synonyms = [];
    if (synonymInput) {
      if (synonymParts.length > 1) {
        showAlert('Synonym must be a single word without spaces.', 'warning');
        return;
      }
      const synonym = synonymParts[0].replace(/[^\w-]/gi, '');
      if (synonym) {
        synonyms = [synonym];
      }
    }

    if (context !== 'common') {
      const existingWord = editingIndex !== null ? wordsData[editingIndex]?.word.toLowerCase() : null;
      const isWordChanged = existingWord && existingWord !== word;
      
      if (!existingWord || isWordChanged) {
        const existsInCommon = commonWords.some(item => item.word === word);
        if (existsInCommon) {
          showAlert('This word already exists in Common list. Common words apply everywhere and cannot be duplicated.', 'warning');
          return;
        }
      }
    }

    if (editingIndex !== null) {
      const updated = [...wordsData];
      updated[editingIndex] = { word, synonyms };
      setWordsData(updated);
      
      setSaving(true);
      setError(null);
      try {
        const wordsOnly = updated.map(item => item.word);
        const wordsWithSynonyms = updated.map(item => ({
          word: item.word,
          synonyms: Array.isArray(item.synonyms) ? item.synonyms : []
        }));

        const urls = getUrls(context);
        const [wordsOnlyResponse, wordsWithSynonymsResponse] = await Promise.all([
          handleJsonToFile({
            url: urls.wordsOnly,
            json: wordsOnly,
            filename: `prohibited_words_${context}.json`
          }),
          handleJsonToFile({
            url: urls.wordsWithSynonyms,
            json: wordsWithSynonyms,
            filename: `prohibited_words_${context}_with_synonyms.json`
          })
        ]);

        if (wordsOnlyResponse?.success && wordsWithSynonymsResponse?.success) {
          showAlert('Word updated successfully.', 'success');
          setOriginalWordsData(wordsWithSynonyms.map(item => ({
            word: item.word,
            synonyms: [...(item.synonyms || [])]
          })));
        } else {
          showAlert('Failed to update word.', 'error');
        }
      } catch (err) {
        console.error(err);
        Sentry.captureException(`270 Error updating word: ${err}`);
        setError(err.message || 'Error updating data.');
        showAlert('Error updating word.', 'error');
      } finally {
        setSaving(false);
      }
    } else {
      const exists = wordsData.some(item => item.word === word);
      if (exists) {
        showAlert('Word already exists.', 'warning');
        return;
      }
      const updated = [...wordsData, { word, synonyms }];
      setWordsData(updated);
      
      setSaving(true);
      setError(null);
      try {
        const wordsOnly = updated.map(item => item.word);
        const wordsWithSynonyms = updated.map(item => ({
          word: item.word,
          synonyms: Array.isArray(item.synonyms) ? item.synonyms : []
        }));

        const urls = getUrls(context);
        const [wordsOnlyResponse, wordsWithSynonymsResponse] = await Promise.all([
          handleJsonToFile({
            url: urls.wordsOnly,
            json: wordsOnly,
            filename: `prohibited_words_${context}.json`
          }),
          handleJsonToFile({
            url: urls.wordsWithSynonyms,
            json: wordsWithSynonyms,
            filename: `prohibited_words_${context}_with_synonyms.json`
          })
        ]);

        if (wordsOnlyResponse?.success && wordsWithSynonymsResponse?.success) {
          showAlert('Word added successfully.', 'success');
          setOriginalWordsData(wordsWithSynonyms.map(item => ({
            word: item.word,
            synonyms: [...(item.synonyms || [])]
          })));
        } else {
          showAlert('Failed to add word.', 'error');
        }
      } catch (err) {
        console.error(err);
        Sentry.captureException(`270 Error adding word: ${err}`);
        setError(err.message || 'Error adding data.');
        showAlert('Error adding word.', 'error');
      } finally {
        setSaving(false);
      }
    }

    handleCloseModal();
  };

  const handleDeleteWord = (index) => {
    confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete "${wordsData[index].word}"?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => {
        const updated = wordsData.filter((_, i) => i !== index);
        setWordsData(updated);
        debouncedSave(updated);
      }
    });
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#fafbfc', minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 3, md: 2 },
            mb: { xs: 3, md: 0 }
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1a1a1a',
                mb: 0.5,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                letterSpacing: '-0.02em'
              }}
            >
              Inappropriate Words Editor
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                textTransform: 'capitalize',
                fontWeight: 500
              }}
            >
              {context.charAt(0).toUpperCase() + context.slice(1)} Context
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search words or synonyms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{
                      mr: 1,
                      color: 'text.secondary',
                      fontSize: '1.2rem'
                    }}
                  />
                )
              }}
              sx={{
                width: { xs: '100%', sm: '250px' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2
                    }
                  }
                }
              }}
            />
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }
                }
              }}
            >
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="alphabetical_asc">Alphabetically Ascending</MenuItem>
                <MenuItem value="alphabetical_desc">Alphabetically Descending</MenuItem>
                <MenuItem value="added_asc">List by Added (Ascending)</MenuItem>
                <MenuItem value="added_desc">List by Added (Descending)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mt: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{
              minWidth: { xs: '100%', sm: '160px' },
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9375rem',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Add Word
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleGenerateAllSynonyms}
            disabled={generatingAllSynonyms || wordsData.length === 0}
            sx={{
              minWidth: { xs: '100%', sm: '200px' },
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9375rem',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)'
              },
              '&:disabled': {
                borderWidth: 2
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {generatingAllSynonyms
              ? `Generating... (${generationProgress.current}/${generationProgress.total})`
              : 'Generate All Synonyms'}
          </Button>
        </Box>
      </Paper>

      {generatingAllSynonyms && generationProgress.total > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.9375rem'
              }}
            >
              Generating Synonyms
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontSize: '0.875rem'
              }}
            >
              {generationProgress.current} of {generationProgress.total} ({Math.round((generationProgress.current / generationProgress.total) * 100)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(generationProgress.current / generationProgress.total) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
              }
            }}
          />
        </Paper>
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mt: 2
        }}
      >
        {sortedAndFilteredWords.map((item, index) => {
          const originalIndex = wordsData.findIndex(w => w.word === item.word && JSON.stringify(w.synonyms) === JSON.stringify(item.synonyms));
          return (
            <Paper
              key={`${item.word}-${originalIndex}-${index}`}
              elevation={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                p: 0.75,
                borderRadius: 1.5,
                border: '1px solid #e0e0e0',
                minWidth: 'fit-content',
                maxWidth: '200px',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main'
                }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, fontSize: '0.75rem', lineHeight: 1.2  , marginRight:"5px"}}>
                  {item.word}
                </Typography>
                {item.synonyms && item.synonyms.length > 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                    {item.synonyms[0]}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                    {/* No synonym */}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto', flexShrink: 0 }}>
                <IconButton
                  size="small"
                  aria-label="edit"
                  onClick={() => handleOpenModal(originalIndex)}
                  sx={{ 
                    color: 'primary.main',
                    padding: '4px',
                    '&:hover': { bgcolor: 'primary.light', color: 'white' },
                    '& .MuiSvgIcon-root': { fontSize: '0.875rem' }
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="delete"
                  onClick={() => handleDeleteWord(originalIndex)}
                  sx={{ 
                    color: 'error.main',
                    padding: '4px',
                    '&:hover': { bgcolor: 'error.light', color: 'white' },
                    '& .MuiSvgIcon-root': { fontSize: '0.875rem' }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {wordsData.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">No words added yet. Click "Add Word" to get started.</Typography>
        </Box>
      )}

      {error && (
        <Box mt={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingIndex !== null ? 'Edit Word' : 'Add Word'}
        </DialogTitle>
        <DialogContent 
          sx={{ 
            pt: 3,
            position: 'relative',
            overflow: 'visible'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Word
            </Typography>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <TextField
                autoFocus
                fullWidth
                variant="outlined"
                value={modalWord}
                onChange={(e) => {
                  setModalWord(e.target.value);
                }}
                helperText="Enter a word or phrase"
                size="small"
                inputRef={wordInputRef}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '60px'
                  }
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  zIndex: 1400
                }}
              >
                <AcentModalWrapper 
                  inputRef={wordInputRef} 
                  setValue={(value) => {
                    setModalWord(value);
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Synonym
            </Typography>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <TextField
                fullWidth
                variant="outlined"
                value={modalSynonyms}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s+/g, '');
                  setModalSynonyms(value);
                }}
                placeholder="Enter a single synonym word"
                helperText="Enter a single word without spaces"
                size="small"
                inputRef={synonymInputRef}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '60px'
                  }
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  zIndex: 1400
                }}
              >
                <AcentModalWrapper 
                  inputRef={synonymInputRef} 
                  setValue={(value) => {
                    const cleanedValue = value.replace(/\s+/g, '');
                    setModalSynonyms(cleanedValue);
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleGenerateSynonyms}
            disabled={generatingSynonyms || !modalWord.trim()}
            variant="outlined"
          >
            {generatingSynonyms ? 'Generating...' : 'Generate Synonym'}
          </Button>
          <Button 
            onClick={handleAddWord} 
            variant="contained" 
            color="primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (editingIndex !== null ? 'Update' : 'Add')}
        </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
